/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { css } from "@linaria/core";
import React, { type Ref, type RefObject, type ChangeEvent, memo } from "react";
import { forwardRef } from "react";
import { colors, spacings } from "../../../../Theme";

type BaseEquationEditorProps = {
  equation: string;
  inline: boolean;
  setEquation: (equation: string) => void;
};

function EquationEditor(
  { equation, setEquation, inline }: BaseEquationEditorProps,
  forwardedRef: Ref<HTMLInputElement | HTMLTextAreaElement>,
): JSX.Element {
  const onChange = (event: ChangeEvent) => {
    setEquation((event.target as HTMLInputElement).value);
  };

  return inline && forwardedRef instanceof HTMLInputElement ? (
    <span className="EquationEditor_inputBackground">
      <span className="EquationEditor_dollarSign">$</span>
      <input
        className="EquationEditor_inlineEditor"
        value={equation}
        onChange={onChange}
        autoFocus
        ref={forwardedRef as RefObject<HTMLInputElement>}
      />
      <span className="EquationEditor_dollarSign">$</span>
    </span>
  ) : (
    <div className="EquationEditor_inputBackground">
      <span className="EquationEditor_dollarSign">{"$$\n"}</span>
      <textarea
        className={equationEditorBlock}
        value={equation}
        onChange={onChange}
        ref={forwardedRef as RefObject<HTMLTextAreaElement>}
      />
      <span className="EquationEditor_dollarSign">{"\n$$"}</span>
    </div>
  );
}

export default memo(forwardRef(EquationEditor));

const equationEditorBlock = css`
  padding: ${spacings.s};
  border: 1px solid ${colors.titanium500};
  border-radius: ${spacings.xs};
  width: 100%;
  resize: vertical;
  overflow: auto;

  &:focus {
    border: 1px solid ${colors.lightNitrogen500_Main};
  }
`;
